import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  KeyValue,
  Progress,
  Typography,
} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { Theme } from '@mui/material';
import { MutationStatus } from 'react-query';
import { candidateService } from '../../api';
import Declaration from '../../components/Declaration';
import NavigationButtons from '../../components/NavigationButtons';
import RouteKey from '../../routes/key';
import ReviewWithdrawalDataTable from './ReviewWithdrawalDataTable';
import ReviewWithdrawalCandidateParticulars from './ReviewWithdrawalCandidateParticulars';
import ReviewWithdrawalDetails from './ReviewWithdrawalDetails';
import DraftTimeStamp from '../../components/DraftTimeStamp';
import { useGetWithdrawalDraftDateTime } from '../../api/candidate/exam/@candidate/queries-services/queries';
import PayNowProxyDetailsModal from '../subject/components/SubjectAmendment/PayNowProxyDetailsModal';
import { usePostPayNowProxyDetails } from '../../api-refactored/exam-registration/usePostPayNowProxyDetails';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  subheader: {
    marginBottom: theme.spacing(4),
  },
  documentsContainer: {
    marginTop: theme.spacing(1.5),
  },
  documentBadgeContainer: {
    marginTop: theme.spacing(4),
  },
  contentTemplateChildContainer: {
    marginBottom: theme.spacing(4),
  },
  personalDetailsContainer: {
    marginBottom: theme.spacing(4),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(4),
  },
  requestTable: {
    marginBottom: theme.spacing(8),
  },
  buttonsContainer: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(7),
  },
  confirmButtonsContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const { examRegistrations: withdrawalRoute, submitted } = RouteKey.withdrawal.child;
const { withdrawal } = RouteKey.child;

const ReviewWithdrawal = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const [declErr, setDeclErr] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [errMsg, setErrMsg] = useState<string>();
  const [openPayNowProxyModal, setOpenPayNowProxyModal] = useState(false);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const { data: withdrawalDetails } = candidateService.exam.candidate.useGetWithdrawDetails(uuid);

  const { data: timestampData } = useGetWithdrawalDraftDateTime();

  const putConfirmWithdraw = candidateService.exam.candidate.usePutConfirmWithdraw(
    () => {
      history.push(`/${withdrawal}/${withdrawalRoute.route}/${uuid}/${submitted.route}`);
    },
    () => {
      setErrMsg('Error Encountered');
    }
  );

  const { mutate: postPayNowProxyDetails } = usePostPayNowProxyDetails({
    onSuccess: () => {
      putConfirmWithdraw.mutate(uuid, {
        onSuccess: () => {
          setApiButtonStatus('success');
          history.push(`/${withdrawal}/${withdrawalRoute.route}/${uuid}/${submitted.route}`);
        },
        onError: () => setApiButtonStatus('error'),
      });
    },
    onError: () => setApiButtonStatus('error'),
  });

  const handleAgreement = () => {
    setDeclErr(false);
    setIsAgreed(!isAgreed);
  };

  const handleBack = () => {
    history.goBack();
  };

  const handleSubmitApplication = () => {
    if (isAgreed) {
      setOpenConfirmation(true);
    } else {
      setDeclErr(true);
    }
  };

  const confirmationCloseHandler = () => setOpenConfirmation(false);
  const confirmWithdraw = () => {
    if (withdrawalDetails?.payNowProxyRequired) {
      setOpenConfirmation(false);
      setOpenPayNowProxyModal(true);
    } else {
      putConfirmWithdraw.mutate(uuid);
    }
  };

  if (!withdrawalDetails) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <ReviewWithdrawalDetails withdrawalDetails={withdrawalDetails} isSectionHeader />
      <ReviewWithdrawalCandidateParticulars withdrawalDetails={withdrawalDetails} />
      <ContentTemplate isDividerBottom continuous type="full">
        <Typography variant="h2" className={classes.subheader}>
          {t('withdrawal->>ReviewWithdrawal.RegisteredSubjects')}
        </Typography>
        <Grid>
          <KeyValue
            id="examYear"
            keyText={t('withdrawal->>ReviewWithdrawal.ExamYear')}
            valueText={withdrawalDetails.examYear}
          />
        </Grid>
        <Grid className={classes.requestTable}>
          <ReviewWithdrawalDataTable withdrawalDetails={withdrawalDetails} />
        </Grid>
        <Typography variant="body1" className={classes.requestTable}>
          {t('withdrawal->>ReviewWithdrawal.RefundMessage')}
        </Typography>
      </ContentTemplate>
      <ContentTemplate continuous type="large">
        <Typography variant="h2" className={classes.subheader}>
          {t('withdrawal->>ReviewWithdrawal.Declaration')}
        </Typography>
        <Declaration
          noTitle
          isAgreed={isAgreed}
          handleAgreement={handleAgreement}
          customContent={t('withdrawal->>ReviewWithdrawal.DeclarationDescription')}
        />
        {declErr && (
          <Grid xs={12} md={8}>
            <Typography className={classes.errorText} display="inline" variant="body2">
              {t('withdrawal->>ReviewWithdrawal.DeclarationError')}
            </Typography>
          </Grid>
        )}
        {errMsg && (
          <Grid xs={12} md={8}>
            <Typography className={classes.errorText} display="inline" variant="body2">
              {errMsg}
            </Typography>
          </Grid>
        )}
        <Box className={classes.buttonsContainer}>
          <NavigationButtons
            backLabel={t('withdrawal->>ReviewWithdrawal.Back')}
            procceedLabel={t('withdrawal->>ReviewWithdrawal.ConfirmWithdrawal')}
            onBackClick={handleBack}
            onProceedClick={handleSubmitApplication}
          />
          {timestampData && <DraftTimeStamp savedAt={timestampData} />}
        </Box>
      </ContentTemplate>

      {openConfirmation && (
        <Dialog open={openConfirmation} onClose={confirmationCloseHandler} maxWidth="sm">
          <DialogTitle>{t('withdrawal->>ReviewWithdrawal.ConfirmTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('withdrawal->>ReviewWithdrawal.ConfirmMessage')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Box className={classes.confirmButtonsContainer}>
              <Button
                id="button-cancel-withdrawal"
                onClick={confirmationCloseHandler}
                variant="text"
              >
                {t('withdrawal->>ReviewWithdrawal.Cancel')}
              </Button>
              <Button id="button-proceed-withdrawal" type="button" onClick={confirmWithdraw}>
                {t('withdrawal->>ReviewWithdrawal.Proceed')}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
      <PayNowProxyDetailsModal
        modalState={openPayNowProxyModal}
        setOpenModalState={setOpenPayNowProxyModal}
        postPayNowProxyDetails={postPayNowProxyDetails}
        applicationUuid={uuid}
        apiButtonStatus={apiButtonStatus}
        setApiButtonStatus={setApiButtonStatus}
      />
    </>
  );
};

export default ReviewWithdrawal;
